<template>
  <v-card class="pb-4 rounded-10" height="100%" style="overflow-y: auto">
    <v-card-text>
      <v-stepper
        v-model="_currentStepIndex"
        vertical
        height="100%"
        flat
        class="pa-0"
      >
        <template v-for="(item, index) in items">
          <v-stepper-step
            :key="`step${index}`"
            :step="index + 1"
            :complete="completedSteps.includes(index + 1)"
            :color="completedSteps.includes(index + 1) ? 'success' : 'primary'"
            edit-icon="mdi-check"
            :editable="editable(index)"
            class="title-font pb-5"
            style="font-size: 17px"
          >
            {{ item.title }}
          </v-stepper-step>
          <v-stepper-content
            :step="index + 1"
            :key="`content${index}`"
            class="pt-0"
          >
            <component
              :is="item.component"
              :prop="item.prop"
              :completed="completedSteps.includes(index + 1)"
              @next="setCurrentStepIndex(currentStepIndex + 1)"
              @step="setStep"
            />
          </v-stepper-content>
        </template>
      </v-stepper>
    </v-card-text>
  </v-card>
</template>
<script>
import { mapActions, mapGetters } from "vuex"
import ChooseKit from "./MainCard/ChooseKit.vue"
import ChooseDentate from "./MainCard/ChooseDentate.vue"
import KitAuthentication from "./MainCard/KitAuthentication.vue"
import ClientName from "./MainCard/ClientName.vue"
import PatientId from "./MainCard/PatientId.vue"
import CaseType from "./MainCard/CaseType.vue"
import UploadScans from "./MainCard/UploadScans.vue"
import ScanAnalysis from "./MainCard/ScanAnalysis.vue"
import UploadFiles from "./MainCard/UploadFiles.vue"
import RxForm from "./MainCard/RxForm.vue"
import SubmitOrder from "./MainCard/SubmitOrder.vue"
import NewOrExistingPatient from "./MainCard/NewOrExistingPatient.vue"

export default {
  name: "MainCard",

  components: {
    KitAuthentication,
    ChooseKit,
    ChooseDentate,
    ClientName,
    PatientId,
    CaseType,
    UploadScans,
    ScanAnalysis,
    UploadFiles,
    RxForm,
    SubmitOrder,
    NewOrExistingPatient
  },

  data() {
    return {
      completedSteps: []
    }
  },

  watch: {
    currentStepIndex(step) {
      const openStep = this.items[step - 1]

      if (!this.completedSteps.includes(step - 1) && step !== 1)
        this.completedSteps.push(step - 1)

      if (openStep.component === RxForm) {
        return this.setPageState([
          { cols: this.$vuetify.breakpoint.lgAndUp ? 2 : 1 },
          { cols: 5, component: "OrderForm", prop: openStep.prop }
        ])
      }

      if (openStep.component === ScanAnalysis) {
        this.setTransition(true)
        const dentate = openStep.prop || this.orderDetails.dentate
        var component = this[`${dentate}ValidateScansResponse`]
          ? "ValidatedDiv"
          : "SelectionDivs"
        return this.setPageState([
          {
            cols: this.$vuetify.breakpoint.lgAndUp ? 7 : 5,
            component,
            prop: dentate
          }
        ])
      }

      return this.setPageState([
        {
          cols: this.$vuetify.breakpoint.lgAndUp ? 7 : 5,
          component: "ConnectBranding"
        }
      ])
    }
  },

  methods: {
    ...mapActions(["setPageState", "setTransition", "setCurrentStepIndex"]),

    setStep(componentName, dentate = null) {
      const stepIndex = this.items.findIndex((i) => {
        const nameMatch = i.component.name === componentName
        if (dentate && i.prop) {
          if (dentate === i.prop) return nameMatch
          return
        }
        return nameMatch
      })
      this.setCurrentStepIndex(stepIndex + 1)
    },

    editable(index) {
      let openStep = this.items[this.currentStepIndex - 1]

      if (
        openStep.component.name === "PatientId" ||
        openStep.component.name === "NewOrExistingPatient"
      ) {
        if (!this.orderDetails.patient_reference) return false
      }
      if (
        this.completedSteps.includes(index + 1) ||
        (index === this.completedSteps.length &&
          this.currentStepIndex !== index + 1)
      ) {
        return true
      }

      return false
    }
  },

  computed: {
    ...mapGetters([
      "settings",
      "orderDetails",
      "upperValidateScansResponse",
      "lowerValidateScansResponse",
      "currentStepIndex"
    ]),

    _currentStepIndex: {
      get() {
        return this.currentStepIndex
      },
      set(payload) {
        this.setCurrentStepIndex(payload)
      }
    },

    dentate() {
      return this.orderDetails.dentate
    },

    items() {
      const components = []

      if (this.settings.manage_clients) {
        components.push({
          title: "CHOOSE SCAN GAUGE KIT",
          component: ChooseKit
        })
      } else {
        components.push(
          {
            title: "ENTER SCAN GAUGE KIT SERIAL NUMBER",
            component: KitAuthentication
          },
          {
            title: "YOUR DETAILS",
            component: ClientName
          }
        )
      }
      if (this.settings.immediate_workflow) {
        components.push({
          title: "CHOOSE NEW OR EXISTING PATIENT",
          component: NewOrExistingPatient
        })
      }

      components.push({
        title: "PATIENT ID",
        component: PatientId
      })

      if (this.settings.immediate_workflow)
        components.push({ title: "CHOOSE CASE TYPE", component: CaseType })

      components.push({ title: "CHOOSE DENTATE", component: ChooseDentate })

      if (this.dentate !== "both")
        components.push(
          { title: `UPLOAD GAUGE SCANS`, component: UploadScans },
          { title: `GAUGE SCAN ANALYSIS`, component: ScanAnalysis },
          { title: `RX FORM`, component: RxForm },
          {
            title: `UPLOAD ADDITIONAL FILES`,
            component: UploadFiles
          }
        )
      else
        for (let item of ["UPPER", "LOWER"])
          components.push(
            {
              title: `${item} - UPLOAD GAUGE SCANS`,
              component: UploadScans,
              prop: item.toLowerCase()
            },
            {
              title: `${item} - GAUGE SCAN ANALYSIS`,
              component: ScanAnalysis,
              prop: item.toLowerCase()
            },
            {
              title: `${item} - UPLOAD ADDITIONAL FILES`,
              component: UploadFiles,
              prop: item.toLowerCase()
            },
            {
              title: `${item} - RX FORM`,
              component: RxForm,
              prop: item.toLowerCase()
            }
          )

      // if (["upper", "both"].includes(this.dentate))
      //   components.push(
      //     { title: `UPPER - UPLOAD GAUGE SCANS`, component: UploadScans },
      //     { title: `UPPER - GAUGE SCAN ANALYSIS`, component: ScanAnalysis },
      //     { title: `UPPER - RX FORM`, component: RxForm }
      //   )
      // if (["lower", "both"].includes(this.dentate))
      //   components.push(
      //     { title: `LOWER - UPLOAD GAUGE SCANS`, component: UploadScans },
      //     { title: `LOWER - GAUGE SCAN ANALYSIS`, component: ScanAnalysis },
      //     { title: `LOWER - RX FORM`, component: RxForm }
      //   )

      components.push({
        title: "SUBMIT",
        component: SubmitOrder
      })

      return components
    }
  }
}
</script>
